import React, { Suspense } from"react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from"react-router-dom";
import { Toaster } from"react-hot-toast";
import { AuthProvider, useAuth } from"./contexts/AuthContext";
import { AppProvider } from"./contexts/AppContext";
import Header from"./components/Header";
import"./App.css";

// 코드 스플리팅 - 필요할 때만 로드
const Login = React.lazy(() => import("./components/Login"));
const Signup = React.lazy(() => import("./components/Signup"));
const ForgotPassword = React.lazy(() => import("./components/ForgotPassword"));
const EmailVerification = React.lazy(() =>
  import("./components/EmailVerification")
);

const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Profile = React.lazy(() => import("./pages/Dashboard/Profile"));
const Plan = React.lazy(() => import("./pages/Dashboard/Plan"));
const ChatWindow = React.lazy(() => import("./components/chat/ChatWindow"));
const AdminView = React.lazy(() => import("./components/AdminView"));
const UserView = React.lazy(() => import("./components/UserView"));
const ProductSelection = React.lazy(() => import("./components/ProductSelection"));
const T5View = React.lazy(() => import("./components/T5View"));
const H8View = React.lazy(() => import("./components/H8View"));

// 빠른 로딩을 위한 미니멀 스켈레톤 컴포넌트
const PageSkeleton = () => (
  <div className="min-h-screen animate-pulse">
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="h-6 bg-gray-200 rounded w-32"></div>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="space-y-4">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      </div>
    </div>
  </div>
);

// 보호된 라우트 컴포넌트
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <PageSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    // 권한이 없으면 해당 역할의 기본 페이지로 리다이렉트
    if (user?.role ==="admin") {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/chat" replace />;
    }
  }

  return children;
};

// 권한별 리다이렉트 컴포넌트 - 제품 선택 페이지로 먼저 이동
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageSkeleton />;
  }

  // 모든 사용자는 먼저 제품 선택 페이지로 이동
  return <Navigate to="/products" replace />;
};

function AppContent() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Header를 숨길 경로들: 로그인 관련 페이지만
  const hideHeaderPaths = ["/login","/signup","/forgot-password","/verify"];
  const isHeaderHidden = hideHeaderPaths.includes(location.pathname);

  // 전체 화면을 사용하는 페이지들 (padding 없음)
  const fullScreenPaths = ["/chat","/t5","/h8"].includes(location.pathname);
  const isFullScreen = fullScreenPaths;

  const needsScroll = ["/dashboard","/dashboard/profile","/dashboard/plan","/admin",
  ].includes(location.pathname);

  return (
    <div
      className={`h-screen transition-colors duration-300 ${
        needsScroll ?"overflow-y-auto" :"overflow-hidden"
      }`}
    >
      {isAuthenticated && !isHeaderHidden && <Header />}
      <main
        className={
          isHeaderHidden
            ?""
            : isFullScreen
            ?""
            :"max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        }
      >
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            {/* 인증 관련 페이지 */}
            <Route
              path="/login"
              element={isAuthenticated ? <RoleBasedRedirect /> : <Login />}
            />
            <Route
              path="/signup"
              element={isAuthenticated ? <RoleBasedRedirect /> : <Signup />}
            />
            <Route
              path="/forgot-password"
              element={
                isAuthenticated ? <RoleBasedRedirect /> : <ForgotPassword />
              }
            />
            <Route path="/verify" element={<EmailVerification />} />

            {/* 보호된 라우트 */}

            {/* 제품 선택 페이지 */}
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <ProductSelection />
                </ProtectedRoute>
              }
            />

            {/* T5 엔진 페이지 */}
            <Route
              path="/t5"
              element={
                <ProtectedRoute>
                  <T5View />
                </ProtectedRoute>
              }
            />

            {/* H8 엔진 페이지 */}
            <Route
              path="/h8"
              element={
                <ProtectedRoute>
                  <H8View />
                </ProtectedRoute>
              }
            />

            {/* 관리자 라우트 */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminView />
                </ProtectedRoute>
              }
            />

            {/* 일반 사용자 라우트 (호환성) */}
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <UserView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/plan"
              element={
                <ProtectedRoute>
                  <Plan />
                </ProtectedRoute>
              }
            />

            {/* 루트 경로 - 인증 상태에 따른 리다이렉트 */}
            <Route
              path="/"
              element={isAuthenticated ? <RoleBasedRedirect /> : <Navigate to="/login" replace />}
            />

            {/* 404 페이지 - 인증 상태에 따른 리다이렉트 */}
            <Route
              path="*"
              element={isAuthenticated ? <RoleBasedRedirect /> : <Navigate to="/login" replace />}
            />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
            <Router>
              <AppContent />
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  className:"",
                  style: {
                    background:"var(--toaster-bg, #ffffff)",
                    color:"var(--toaster-color, #374151)",
                    border:"1px solid var(--toaster-border, #e5e7eb)",
                  },
                  success: {
                    className:"",
                    style: {
                      background:"var(--toaster-success-bg, #f0fdf4)",
                      color:"var(--toaster-success-color, #15803d)",
                      border:"1px solid var(--toaster-success-border, #bbf7d0)",
                    },
                  },
                  error: {
                    className:"",
                    style: {
                      background:"var(--toaster-error-bg, #fef2f2)",
                      color:"var(--toaster-error-color, #dc2626)",
                      border:"1px solid var(--toaster-error-border, #fecaca)",
                    },
                  },
                }}
              />
            </Router>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
